import React, { createContext, useContext, useState, useCallback, useMemo, FC, ReactNode, PropsWithChildren, useRef, useEffect } from 'react';
import { View } from 'react-native';
import { Position, TimeCategory } from '@interfaces/base';
import { isWeb } from '@helpers';
import { useBottomSheetAnimation } from '@components/common/ScreenAnimations/components/BottomSheet/AnimationContext';
import { CurveLinesContextValue, CurveLinesPositions, CurveLinesContainerProps } from '@v2/pages/common/components/NCFileBatchEvents/types';

const CurveLinesContext = createContext<CurveLinesContextValue | null>(null);

const RESIZE_SETTLE_DELAY_MS = 250;

export const CurveLinesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [positions, setPositions] = useState<CurveLinesPositions>({
    tileDots: {},
    markerPositions: {},
    containerOffset: null,
  });
  const [measurementVersion, setMeasurementVersion] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hasSettledOnce, setHasSettledOnce] = useState(false);
  const { isAnimationSettled } = useBottomSheetAnimation();

  const bumpMeasurementVersion = useCallback(() => {
    setMeasurementVersion((v) => v + 1);
  }, []);

  // Settled = true is the default when no bottom-sheet provider is above us, so this fires once on mount in that case.
  // On the first settle, wipe any positions captured during the sheet's open animation. The existing
  // containerOffset === null guard in ConnectorLines then blocks rendering until the bumped measurement
  // version re-measures everything from the final layout — preventing a one-frame flicker of stale lines.
  useEffect(() => {
    if (!isAnimationSettled) return;
    if (!hasSettledOnce) {
      setPositions({ tileDots: {}, markerPositions: {}, containerOffset: null });
      setHasSettledOnce(true);
    }
    bumpMeasurementVersion();
  }, [isAnimationSettled, hasSettledOnce, bumpMeasurementVersion]);

  useEffect(() => {
    if (!isWeb) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      setIsResizing(true);
      if (timeoutId != null) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        setPositions({ tileDots: {}, markerPositions: {}, containerOffset: null });
        setIsResizing(false);
        bumpMeasurementVersion();
      }, RESIZE_SETTLE_DELAY_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [bumpMeasurementVersion]);

  const registerTileDot = useCallback((type: TimeCategory, position: Position) => {
    setPositions((prev) => {
      const existing = prev.tileDots[type];
      if (existing?.x === position.x && existing?.y === position.y) {
        return prev;
      }
      return {
        ...prev,
        tileDots: { ...prev.tileDots, [type]: position },
      };
    });
  }, []);

  const registerMarkerPosition = useCallback((type: TimeCategory, position: Position) => {
    setPositions((prev) => {
      const existing = prev.markerPositions[type];
      if (existing?.x === position.x && existing?.y === position.y) {
        return prev;
      }
      return {
        ...prev,
        markerPositions: { ...prev.markerPositions, [type]: position },
      };
    });
  }, []);

  const registerContainerOffset = useCallback((position: Position) => {
    setPositions((prev) => {
      if (prev.containerOffset?.x === position.x && prev.containerOffset?.y === position.y) {
        return prev;
      }
      return {
        ...prev,
        containerOffset: position,
      };
    });
  }, []);

  const resetPositions = useCallback(() => {
    setPositions({
      tileDots: {},
      markerPositions: {},
      containerOffset: null,
    });
  }, []);

  const resetLinePositions = useCallback(() => {
    setPositions((prev) => ({
      ...prev,
      tileDots: {},
      markerPositions: {},
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      positions,
      measurementVersion,
      isResizing,
      hasSettledOnce,
      bumpMeasurementVersion,
      registerTileDot,
      registerMarkerPosition,
      registerContainerOffset,
      resetPositions,
      resetLinePositions,
    }),
    [
      positions,
      measurementVersion,
      isResizing,
      hasSettledOnce,
      bumpMeasurementVersion,
      registerTileDot,
      registerMarkerPosition,
      registerContainerOffset,
      resetPositions,
      resetLinePositions,
    ]
  );

  return <CurveLinesContext.Provider value={contextValue}>{children}</CurveLinesContext.Provider>;
};

export const useCurveLinesContext = () => {
  const context = useContext(CurveLinesContext);
  if (!context) {
    throw new Error('useCurveLinesContext must be used within CurveLinesProvider');
  }
  return context;
};

export const useCurveLinesContextOptional = () => {
  return useContext(CurveLinesContext);
};

export const CurveLinesContainer: FC<PropsWithChildren<CurveLinesContainerProps>> = ({ children, style }) => {
  const containerRef = useRef<View>(null);
  const { registerContainerOffset, resetPositions, measurementVersion } = useCurveLinesContext();

  useEffect(() => {
    resetPositions();
  }, [resetPositions]);

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.measureInWindow((x: number, y: number) => {
      registerContainerOffset({ x, y });
    });
  }, [registerContainerOffset, measurementVersion]);

  return (
    <View ref={containerRef} style={[{ flex: 1 }, style]}>
      {children}
    </View>
  );
};
